import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Edit2, Plus, Save, X } from 'lucide-react';
import { 
  fetchInvoiceItems, 
  addInvoiceItem, 
  updateInvoiceItem, 
  deleteInvoiceItem,
  updateSupplierInvoice 
} from '@/api';
import type { SupplierInvoice } from '@/types/supplier';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

interface EditInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: SupplierInvoice | null;
  onUpdate: () => void;
}

interface InvoiceItem {
  id?: string;
  item_id?: string;
  item_name: string;
  item_sku?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export function EditInvoiceDialog({ open, onOpenChange, invoice, onUpdate }: EditInvoiceDialogProps) {
  const { toast } = useToast();
  const currencySymbol = useCurrencySymbol();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<InvoiceItem>({
    item_name: '',
    quantity: 1,
    unit_price: 0,
    total_price: 0
  });
  const [invoiceData, setInvoiceData] = useState({
    invoice_number: '',
    date: '',
    due_date: '',
    amount: 0
  });

  useEffect(() => {
    if (invoice && open) {
      setInvoiceData({
        invoice_number: invoice.invoiceNumber,
        date: invoice.date,
        due_date: invoice.dueDate,
        amount: invoice.amount
      });
      loadItems();
    }
  }, [invoice, open]);

  const loadItems = async () => {
    if (!invoice) return;
    
    try {
      setLoading(true);
      const response = await fetchInvoiceItems(invoice.id);
      if (response.data?.data) {
        // Ensure all numeric fields are properly parsed
        const parsedItems = response.data.data.map((item: any) => ({
          ...item,
          quantity: Number(item.quantity) || 0,
          unit_price: Number(item.unit_price) || 0,
          total_price: Number(item.total_price) || 0
        }));
        setItems(parsedItems);
      }
    } catch (error) {
      console.error('Failed to load items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!invoice || !newItem.item_name || newItem.quantity <= 0 || newItem.unit_price < 0) {
      toast({
        title: 'Error',
        description: 'Please fill all item fields correctly',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      const itemData = {
        ...newItem,
        total_price: newItem.quantity * newItem.unit_price
      };
      
      await addInvoiceItem(invoice.id, itemData);
      
      toast({
        title: 'Success',
        description: 'Item added successfully'
      });
      
      setNewItem({
        item_name: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0
      });
      
      await loadItems();
      onUpdate();
    } catch (error) {
      console.error('Failed to add item:', error);
      toast({
        title: 'Error',
        description: 'Failed to add item',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (itemId: string, updatedData: Partial<InvoiceItem>) => {
    if (!invoice) return;

    try {
      setLoading(true);
      
      // Calculate new total if quantity or price changed
      let updatePayload: any = { ...updatedData };
      if (updatedData.quantity !== undefined || updatedData.unit_price !== undefined) {
        const currentItem = items.find(i => i.id === itemId);
        if (currentItem) {
          const quantity = updatedData.quantity ?? currentItem.quantity;
          const unitPrice = updatedData.unit_price ?? currentItem.unit_price;
          updatePayload.total_price = quantity * unitPrice;
        }
      }
      
      await updateInvoiceItem(invoice.id, itemId, updatePayload);
      
      toast({
        title: 'Success',
        description: 'Item updated successfully'
      });
      
      setEditingItem(null);
      await loadItems();
      onUpdate();
    } catch (error) {
      console.error('Failed to update item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update item',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!invoice || !confirm('Are you sure you want to delete this item?')) return;

    try {
      setLoading(true);
      await deleteInvoiceItem(invoice.id, itemId);
      
      toast({
        title: 'Success',
        description: 'Item deleted successfully'
      });
      
      await loadItems();
      onUpdate();
    } catch (error) {
      console.error('Failed to delete item:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete item',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInvoice = async () => {
    if (!invoice) return;

    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('supplier_id', invoice.supplierId);
      formData.append('invoice_number', invoiceData.invoice_number);
      formData.append('date', invoiceData.date);
      formData.append('due_date', invoiceData.due_date);
      formData.append('amount', calculateTotal().toString());
      formData.append('status', invoice.status);

      await updateSupplierInvoice(invoice.id, formData);
      
      toast({
        title: 'Success',
        description: 'Invoice updated successfully'
      });
      
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to update invoice',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unit_price) || 0;
      return sum + (quantity * unitPrice);
    }, 0);
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    // Handle null/undefined amounts
    let safeAmount = 0;
    if (typeof amount === 'number') {
      safeAmount = isNaN(amount) ? 0 : amount;
    } else if (typeof amount === 'string') {
      safeAmount = parseFloat(amount) || 0;
    }
    return `${currencySymbol}${safeAmount.toFixed(2)}`;
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Edit Invoice - {invoice.invoiceNumber}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Invoice Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Invoice Number</Label>
              <Input
                value={invoiceData.invoice_number}
                onChange={(e) => setInvoiceData({ ...invoiceData, invoice_number: e.target.value })}
                disabled={loading}
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={invoiceData.date}
                onChange={(e) => setInvoiceData({ ...invoiceData, date: e.target.value })}
                disabled={loading}
              />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={invoiceData.due_date}
                onChange={(e) => setInvoiceData({ ...invoiceData, due_date: e.target.value })}
                disabled={loading}
              />
            </div>
          </div>

          {/* Items Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Invoice Items</h3>
              <Badge variant="outline">
                Total: {formatCurrency(calculateTotal())}
              </Badge>
            </div>

            {/* Add New Item */}
            <div className="mb-4 p-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium mb-3">Add New Item</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                <div className="md:col-span-2">
                  <Label>Item Name</Label>
                  <Input
                    placeholder="Enter item name"
                    value={newItem.item_name}
                    onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newItem.unit_price}
                    onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                    disabled={loading}
                  />
                </div>
                <div>
                  <Button 
                    onClick={handleAddItem} 
                    disabled={loading}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Item Name</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total Price</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No items added yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          {editingItem === item.id ? (
                            <Input
                              value={item.item_name}
                              onChange={(e) => {
                                const updated = items.map(i => 
                                  i.id === item.id ? { ...i, item_name: e.target.value } : i
                                );
                                setItems(updated);
                              }}
                              className="w-full"
                            />
                          ) : (
                            item.item_name
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {editingItem === item.id ? (
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                                const updated = items.map(i => 
                                  i.id === item.id ? { 
                                    ...i, 
                                    quantity: newQuantity,
                                    total_price: newQuantity * (Number(i.unit_price) || 0)
                                  } : i
                                );
                                setItems(updated);
                              }}
                              className="w-20 mx-auto"
                            />
                          ) : (
                            item.quantity
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editingItem === item.id ? (
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => {
                                const newUnitPrice = Math.max(0, parseFloat(e.target.value) || 0);
                                const updated = items.map(i => 
                                  i.id === item.id ? { 
                                    ...i, 
                                    unit_price: newUnitPrice,
                                    total_price: (Number(i.quantity) || 0) * newUnitPrice
                                  } : i
                                );
                                setItems(updated);
                              }}
                              className="w-24 ml-auto"
                            />
                          ) : (
                            formatCurrency(item.unit_price)
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(editingItem === item.id ? 
                            (Number(item.quantity) || 0) * (Number(item.unit_price) || 0) : 
                            item.total_price)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            {editingItem === item.id ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    if (item.id) {
                                      handleUpdateItem(item.id, {
                                        item_name: item.item_name,
                                        quantity: item.quantity,
                                        unit_price: item.unit_price
                                      });
                                    }
                                  }}
                                  disabled={loading}
                                  variant="default"
                                >
                                  <Save className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setEditingItem(null);
                                    loadItems(); // Reset to original values
                                  }}
                                  disabled={loading}
                                  variant="outline"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => setEditingItem(item.id || null)}
                                  disabled={loading}
                                  variant="outline"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => item.id && handleDeleteItem(item.id)}
                                  disabled={loading}
                                  variant="destructive"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleUpdateInvoice} disabled={loading}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}